import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ShoppingBag, Menu, X, User, LogOut, 
  Truck, Calculator, Palette, ShieldCheck, Heart
} from 'lucide-react';
import { useCart } from './CartContext'; // Asegúrate de que CartContext.js esté en la misma carpeta
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios'; 

const Header = () => {
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);

  // 1. Detectar Scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 2. Sincronización de Sesión (Tiempo Real)
  useEffect(() => {
    const syncSession = async () => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        // Carga inicial rápida (caché)
        if (storedUser) {
            try { setUser(JSON.parse(storedUser)); } catch (e) { setUser(null); }
        } else {
            setUser(null);
        }

        // Verificación con Servidor (Datos Frescos)
        if (token) {
            try {
                const { data: freshUser } = await api.get('/auth/me');
                setUser(freshUser);
                localStorage.setItem('user', JSON.stringify(freshUser));
            } catch (error) {
                // Si el token expiró, cerrar sesión silenciosamente
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setUser(null);
                }
            }
        }
    };

    syncSession();
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate('/');
    window.location.reload();
  };

  // --- 3. CONFIGURACIÓN DINÁMICA DE BOTÓN ---
  const getRoleConfig = () => {
    if (!user) return null;

    // A. Verificar permiso maestro de Dashboard
    const hasDashboardAccess = 
        (user.permisos && user.permisos.dashboard === true) || 
        user.rol === 'admin' || 
        user.email === 'dolce.fleur.2026@gmail.com';

    // B. Configuración por defecto (Cliente)
    let config = { 
        label: 'Mi Cuenta', 
        color: 'from-orange-400 to-pink-500', 
        icon: Heart, 
        path: '/mi-cuenta' 
    };

    // C. Si tiene acceso al panel, cambiamos la ruta y el estilo según el rol (estético)
    if (hasDashboardAccess) {
        config.path = '/admin-panel';
        config.label = 'Admin Panel';
        config.color = 'from-purple-600 to-indigo-600';
        config.icon = ShieldCheck;

        // Personalización visual por rol (Opcional, pero se ve bien)
        if (user.rol === 'logistica' || user.rol === 'chofer') {
            config.label = 'Logística';
            config.color = 'from-blue-600 to-cyan-600';
            config.icon = Truck;
        } else if (user.rol === 'florista') {
            config.label = 'Taller Floral';
            config.color = 'from-pink-500 to-rose-500';
            config.icon = Palette;
        } else if (user.rol === 'vendedor') {
            config.label = 'Ventas';
            config.color = 'from-green-500 to-emerald-600';
            config.icon = Calculator;
        }
    }

    return config;
  };
  
  const roleConfig = getRoleConfig();

  const navLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Catálogo', path: '/catalogo' },
    { name: 'Arma tu Ramo', path: '/cotizar/crea-tu-ramo' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/90 backdrop-blur-xl shadow-lg py-2 border-b border-white/20' 
          : 'bg-white py-4 shadow-sm'
      }`}
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        
        {/* LOGO */}
        <Link to="/" className="flex items-center gap-3 group">
          <motion.div whileHover={{ rotate: 10, scale: 1.1 }} className="relative w-10 h-10">
            <div className="absolute inset-0 bg-orange-400 blur-lg opacity-40 rounded-full group-hover:opacity-70 transition-opacity"/>
            <img 
                src="/logo.png" 
                onError={(e) => e.target.src='https://cdn-icons-png.flaticon.com/512/740/740845.png'} 
                alt="Dolce Fleur" 
                className="relative w-full h-full object-contain drop-shadow-sm" 
            />
          </motion.div>
          <div className="leading-tight">
            <span className="text-xl font-black tracking-tight text-gray-900 block">
              Dolce<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-pink-600">Fleur</span>
            </span>
          </div>
        </Link>

        {/* NAV DESKTOP */}
        <nav className="hidden md:flex items-center gap-1 bg-gray-100/50 p-1 rounded-full border border-gray-200">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all relative ${
                location.pathname === link.path ? 'text-gray-900 bg-white shadow-sm' : 'text-gray-500 hover:text-orange-500 hover:bg-white/50'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* USUARIO Y CARRITO */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/carrito" className="relative p-2.5 bg-gray-50 hover:bg-orange-50 rounded-full transition-colors group">
            <ShoppingBag size={20} className="text-gray-600 group-hover:text-orange-500" />
            <AnimatePresence>
              {count > 0 && (
                <motion.span 
                  initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full shadow-sm border-2 border-white"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>

          {user ? (
            <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
              {/* LINK DINÁMICO */}
              <Link to={roleConfig?.path} className="hidden lg:flex flex-col items-end cursor-pointer group">
                <span className="text-xs font-bold text-gray-900 group-hover:text-orange-500 transition-colors">
                  {user.nombre?.split(' ')[0] || "Usuario"}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 px-2 py-0.5 rounded-full text-white bg-gradient-to-r ${roleConfig?.color}`}>
                  {roleConfig?.icon && <roleConfig.icon size={8} />} {roleConfig?.label}
                </span>
              </Link>
              
              {/* AVATAR + LOGOUT */}
              <div className="relative group">
                <Link to={roleConfig?.path}>
                  <div className={`w-10 h-10 rounded-full p-[2px] bg-gradient-to-br ${roleConfig?.color || 'from-gray-200 to-gray-300'}`}>
                    <img 
                        src={user.foto_url || `https://ui-avatars.com/api/?name=${user.nombre}&background=ffedd5&color=db2777`} 
                        className="w-full h-full rounded-full object-cover border-2 border-white bg-white" 
                        alt="avatar"
                    />
                  </div>
                </Link>
                <button onClick={handleLogout} className="absolute top-12 right-0 w-32 bg-white p-2 rounded-xl shadow-xl border border-gray-100 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all flex items-center justify-center gap-2 text-red-500 font-bold text-xs hover:bg-red-50 z-50">
                  <LogOut size={14}/> Salir
                </button>
              </div>
            </div>
          ) : (
            <Link to="/login" className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-full font-bold text-sm hover:bg-gray-800 transition-all shadow-lg hover:scale-105">
              <User size={16} /> Entrar
            </Link>
          )}
        </div>

        {/* MOBILE BTN */}
        <button className="md:hidden text-gray-800 p-2" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 overflow-hidden shadow-2xl absolute top-full left-0 w-full"
          >
            <nav className="flex flex-col p-6 space-y-2">
              {navLinks.map((link) => (
                <Link key={link.name} to={link.path} onClick={() => setIsOpen(false)} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 text-gray-800 font-bold">
                  {link.name} 
                </Link>
              ))}
              <hr className="border-gray-100 my-2" />
              
              {user ? (
                <>
                  <Link to={roleConfig?.path} onClick={() => setIsOpen(false)} className={`flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r ${roleConfig?.color} text-white font-bold shadow-lg`}>
                    <div className="bg-white/20 p-2 rounded-lg">
                        {roleConfig?.icon && <roleConfig.icon size={20}/>}
                    </div>
                    <div>
                        <p className="text-xs opacity-90 font-medium uppercase">Ir a</p>
                        <p className="text-lg">{roleConfig?.label}</p>
                    </div>
                  </Link>
                  <button onClick={() => { handleLogout(); setIsOpen(false); }} className="w-full p-3 text-red-500 font-bold bg-red-50 rounded-xl flex items-center justify-center gap-2 mt-2 border border-red-100">
                    <LogOut size={18}/> Cerrar Sesión
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)} className="bg-gray-900 text-white text-center py-4 rounded-xl font-bold block shadow-lg">
                    Iniciar Sesión
                </Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;